import {useMemo, useState} from "react"
import {Pencil, Search, TriangleAlert} from "lucide-react"
import {generatePath, NavLink, useLocation, useNavigate} from "react-router-dom"
import type {OtkDocumentCounts, Sort} from "shared"

import {AnnulDialog} from "@/components/documents/AnnulDialog"
import {buildDocumentColumns} from "@/components/documents/columns"
import {DeleteDraftDialog} from "@/components/documents/DeleteDraftDialog"
import {DuplicateDialog} from "@/components/documents/DuplicateDialog"
import {UnsentRowActions} from "@/components/documents/UnsentDocumentActions"
import {DataTable} from "@/components/DataTable"
import {Badge} from "@/components/ui/badge"
import {Button} from "@/components/ui/button"
import {Card, CardContent} from "@/components/ui/card"
import {InputGroup, InputGroupAddon, InputGroupInput,} from "@/components/ui/input-group"
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs"
import {
  canAnnul,
  canDuplicate,
  canRetrySend,
  documentMatchesQuickSearch,
  type DocumentRow,
  documentTabCountStatus,
  documentTabLabels,
  type DocumentTabStatus,
  documentTabStatuses,
} from "@/lib/documents"

// documents.md: tabs rendered only while the пункт actually has documents in
// that status. `НЕ ВІДПРАВЛЕНО` is the only one so far.
const CONDITIONAL_STATUS_TABS: readonly DocumentTabStatus[] = ["НЕ ВІДПРАВЛЕНО"]

/**
 * documents.md's main-page `DocumentsList` — status tabs, quick search, and
 * the `DataTable`/empty-state pair, inside the shared `Card`/`CardContent`.
 * `documents` is the caller's own already tab+advanced-search-filtered
 * dataset; the active tab is router-owned by `Documents.tsx` (`tab`/
 * `onTabChange`), not local state here — only the quick-search text stays
 * page-local, not URL-persisted. Tab counters come from capped backend counts.
 */
export function DocumentsList({
  activeRowId,
  counts,
  documents,
  hasMore = false,
  hasUnsent = false,
  initialSearch = "",
  isLoading = false,
  isLoadingMore = false,
  onAnnul,
  onDelete,
  onDuplicate,
  onLoadMore,
  onRetrySend,
  onSortChange,
  onTabChange,
  sort,
  tab,
}: {
  /** The document currently open in the modal above this list — highlighted per documents.md. */
  activeRowId?: string
  counts: OtkDocumentCounts | null
  documents: DocumentRow[]
  /** Whether the active tab has another page available — shows the "Завантажити ще" button. */
  hasMore?: boolean
  /** Whether any `НЕ ВІДПРАВЛЕНО` document exists; derived from the tab counts. */
  hasUnsent?: boolean
  /** Seeds the quick-search box from the advanced-search form's own "Номер документа" field (both round-trip through the same `q` query param) — editable from there on, like any other quick-search edit. */
  initialSearch?: string
  /** documents.md's "Завантаження таблиці документів" — the table's own skeleton stays while tabs/search chrome around it stays put. */
  isLoading?: boolean
  /** Disables/labels the "Завантажити ще" button while its own request is in flight. */
  isLoadingMore?: boolean
  onAnnul: (id: string, reason: string) => void | Promise<void>
  onDelete: (id: string, version: number) => void | Promise<void>
  /** Creates the duplicate and resolves to its new id — see `store/documents.ts`'s `duplicate`. */
  onDuplicate: (id: string, sectionIds: string[]) => Promise<string>
  onLoadMore: () => void
  /** `POST /otk/documents/:id/retry` — re-sends the already-signed package. Resolves once the store's own refetch has settled. */
  onRetrySend: (id: string) => Promise<void>
  /** Toggles the `Створено` column's backend sort direction — see `columns.tsx`'s `createdSort`. */
  onSortChange: (sort: Sort) => void
  onTabChange: (tab: DocumentTabStatus) => void
  /** The `Створено` column's current backend sort direction. */
  sort: Sort
  tab: DocumentTabStatus
}) {
  const [search, setSearch] = useState(initialSearch)
  const navigate = useNavigate()
  const location = useLocation()


  const visibleTabs = useMemo(
    () =>
      documentTabStatuses.filter(
        (status) =>
          !CONDITIONAL_STATUS_TABS.includes(status) ||
          hasUnsent ||
          // Never hide the tab the user is standing on mid-session: the
          // fallback to «Чернетка» is the caller's call (`Documents.tsx`),
          // and dropping the trigger first would leave `Tabs` with a value
          // no trigger matches.
          status === tab
      ),
    [hasUnsent, tab]
  )

  const visibleRows = useMemo(
    () => documents.filter((doc) => documentMatchesQuickSearch(doc, search)),
    [documents, search]
  )

  const columns = useMemo(
    () =>
      buildDocumentColumns({
        createdSort: {
          direction: sort === "DESC" ? "desc" : "asc",
          onToggle: () => onSortChange(sort === "DESC" ? "ASC" : "DESC"),
        },
        // Preserves `?tab=`/`?sort=`/`?q=` (`Documents.tsx`'s own router
        // state) across the row → modal navigation, so `DocumentModalRoute`'s
        // close handler (`{ pathname: base, search: location.search }`)
        // restores the same tab instead of falling back to the default.
        linkTo: (id) => ({ pathname: `/documents/${id}`, search: location.search }),
      }),
    [location.search, onSortChange, sort]
  )

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Tabs
            onValueChange={(value) => onTabChange(value as DocumentTabStatus)}
            value={tab}
          >
            <TabsList className="!h-10 justify-start">
              {visibleTabs.map((status) => (
                <TabsTrigger className="px-4 text-sm" key={status} value={status}>
                  {status === "НЕ ВІДПРАВЛЕНО" && (
                    <TriangleAlert className="size-4 text-warning" />
                  )}
                  {documentTabLabels[status]}
                  {counts && <Badge variant="secondary">{counts[documentTabCountStatus[status]]}</Badge>}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button asChild className="px-0" variant="link">
            <NavLink to="/documents/search">
              <Search />
              Розширений Пошук
            </NavLink>
          </Button>
        </div>

        <InputGroup>
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            aria-label="Швидкий пошук документів"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Номер документа, держ. номер або VIN"
            value={search}
          />
        </InputGroup>

        {!isLoading && visibleRows.length === 0 ? (
          <div className="grid min-h-56 place-items-center rounded-md border border-dashed text-center">
            <div>
              <p className="font-medium">Документів не знайдено</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Оберіть інший статус або уточніть параметри розширеного пошуку.
              </p>
            </div>
          </div>
        ) : (
          <DataTable
            activeRowId={activeRowId}
            columns={columns}
            data={visibleRows}
            enablePagination={false}
            fillAvailableHeight
            footer={
              hasMore && (
                <div className="flex justify-center border-t p-3">
                  <Button
                    disabled={isLoadingMore}
                    onClick={onLoadMore}
                    variant="outline"
                  >
                    {isLoadingMore ? "Завантаження…" : "Завантажити ще"}
                  </Button>
                </div>
              )
            }
            getRowId={(row) => row.id}
            isLoading={isLoading}
            rowActions={(row) => (
              <div className="flex justify-end gap-2">
                {canRetrySend(row.original.status) && (
                  <UnsentRowActions
                    document={row.original}
                    onRetrySend={onRetrySend}
                  />
                )}
                {row.original.status === "ЧЕРНЕТКА" && (
                  <Button asChild size="sm" variant="outline">
                    <NavLink to={`/documents/edit/${row.original.id}`}>
                      <Pencil data-icon="inline-start" />
                      Редагувати
                    </NavLink>
                  </Button>
                )}
                {row.original.status === "ЧЕРНЕТКА" && (
                  <DeleteDraftDialog
                    documentNumber={row.original.number}
                    onConfirm={() => onDelete(row.original.id, row.original.version)}
                    triggerSize="sm"
                  />
                )}
                {canAnnul(row.original.status) && row.original.registryAcceptedAt !== null && (
                  <AnnulDialog
                    documentNumber={row.original.number}
                    onConfirm={(reason) => onAnnul(row.original.id, reason)}
                    triggerSize="sm"
                  />
                )}
                {canDuplicate(row.original.status) && (
                  <DuplicateDialog
                    onConfirm={async (sectionIds) => {
                      const newId = await onDuplicate(row.original.id, sectionIds)
                      // documents.md: "the list is not where the user ends
                      // up" — confirming opens the new draft's editor.
                      navigate(generatePath("/documents/edit/:id", { id: newId }))
                    }}
                    triggerSize="sm"
                  />
                )}
              </div>
            )}
            tableClassName="table-fixed"
          />
        )}
      </CardContent>
    </Card>
  )
}
